import { Box, Stack } from "@mui/material";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode } from "react";

interface CreditCategoryCardProps {
  readonly iconType: "calendar-navigation" | "clock";
  readonly title: string;
  readonly text: ReactNode;
}

function CreditCategoryCard(props: CreditCategoryCardProps) {
  const { iconType, title, text } = props;

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: "16px",
        padding: theme.spacing(4),
        boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: theme.spacing(3),
        width: "100%",
      })}
    >
      <CbhIcon
        type={iconType}
        size="medium"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          flexShrink: 0,
        })}
      />
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Text sx={{ fontWeight: 500, textAlign: "left" }}>{title}</Text>
        {text}
      </Stack>
    </Box>
  );
}

export function OneCreditEachStep() {
  // This stage assumes `creditCap == 1` for both credit types.
  // If the feature flag is updated to allow more than 1 credit, it needs to be updated.
  return (
    <>
      <Text variant="h3" sx={{ fontWeight: 400 }}>
        One credit for each category
      </Text>
      <Text sx={{ fontWeight: 400 }} color={(theme) => theme.palette.text.secondary}>
        You can hold one credit at a time, for each category
      </Text>
      <Stack spacing={3} sx={{ width: "100%" }}>
        <CreditCategoryCard
          iconType="calendar-navigation"
          title="Same-day cancellation credit"
          text={
            <Text
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 400,
                textAlign: "left",
              })}
            >
              Used when you cancel a shift with &lt;24h notice.{" "}
              <Box
                component="span"
                sx={(theme) => ({ color: theme.palette.error.main, fontWeight: 400 })}
              >
                Does not apply to cancellations after a shift has started, which are considered no
                shows.
              </Box>
            </Text>
          }
        />
        <CreditCategoryCard
          iconType="clock"
          title="Lateness credit"
          text={
            <Text
              variant="body2"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 400,
                textAlign: "left",
              })}
            >
              Used when you lose points by arriving more than 15 minutes late to a shift.
            </Text>
          }
        />
      </Stack>
    </>
  );
}
